import React, { useState } from 'react';

import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';

import {
  Button,
  Snackbar,
  TextField,
  Typography,
} from '../../../components/atoms';
import { Dialog } from '../../../components/layouts';
import { setEmailAddress } from '../../../core/state/user/userMiddleware';
import { checkUtils } from '../../../utils';
import * as Api from '../../../weborder/Api';
import { AxiosError } from '../../../weborder/services/apiServices/apiServices';

interface Props {
  Close: () => any;
  EmailRequestMode: number;
  ShowEmailDialog: boolean;
  translate: any;
  UpdateCustomer: () => any;
}

const useStyles = makeStyles((theme: Theme) => ({
  dialog: {
    '& .MuiDialogTitle-root': {
      padding: theme.spacing(2.25, 0),
    },

    '& .fd-text__h3': {
      marginBottom: theme.spacing(1.75),

      [theme.breakpoints.up('sm')]: {
        marginBottom: theme.spacing(2.5),
      },
    },
  },
}));

export function EmailDialog(props: Props): JSX.Element {
  const {
    Close: handleClose,
    EmailRequestMode: emailRequestMode,
    ShowEmailDialog: showEmailDialog,
    translate,
    UpdateCustomer: updateCustomer,
  } = props;
  const classes = useStyles();

  const [email, setEmail] = useState('');
  const [submitError, setSubmitError] = useState<string>();

  const handleEmailChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    setEmail(event.target.value);
  };

  const handleKeyPress = (
    target: React.KeyboardEvent<HTMLInputElement>,
  ): void => {
    if (target.charCode === 13) {
      handleEmailSubmit();
    }
  };

  const handleDismissDialog = (): void => {
    localStorage.setItem('dismissedDialog', 'true');
    handleClose();
  };

  const handleEmailSubmit = (): void => {
    if (email === '') {
      return;
    }

    setEmailAddress(email)
      .then(() => {
        setEmail('');
        handleClose();
        updateCustomer();
      })
      .catch((error: AxiosError) => {
        error.response?.data?.UserMessage &&
          setSubmitError(error.response.data.UserMessage);
      });
  };

  const dismissedDialog =
    localStorage.getItem('dismissedDialog') === 'true' &&
    emailRequestMode === Api.EmailRequestMode.Request;

  const getTitle = (): string =>
    emailRequestMode === Api.EmailRequestMode.Require
      ? translate('We_need_your_email_address_to_process_your_order')
      : translate('Please_enter_your_email_address');

  const noThanksButton = (
    <Button fullWidth key="reject-button" onClick={handleDismissDialog}>
      {translate('No_thanks')}
    </Button>
  );

  const submitButton = (
    <Button
      disabled={!checkUtils.isValidEmail(email)}
      key="submit-button"
      onClick={handleEmailSubmit}
      fullWidth
    >
      {translate('Submit')}
    </Button>
  );

  const getDialogActions = (): JSX.Element => {
    if (emailRequestMode === Api.EmailRequestMode.Require) {
      return submitButton;
    }

    return (
      <>
        {submitButton}
        {noThanksButton}
      </>
    );
  };

  return (
    <Dialog
      actions={getDialogActions()}
      className={classes.dialog}
      disableEscapeKeyDown
      onClose={handleClose}
      open={showEmailDialog && !dismissedDialog}
    >
      <Typography variant="h3">{getTitle()}</Typography>
      <TextField
        aria-label={`${translate('Please_enter_your_email_address')}`}
        autoFocus
        key="email-input"
        onChange={handleEmailChange}
        onKeyPress={handleKeyPress}
        placeholder={translate('Email')}
        type="email"
        value={email}
      />
      {submitError && <Snackbar message={submitError} variant="error" />}
    </Dialog>
  );
}
